<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="translate-x-full opacity-0"
    enter-to-class="translate-x-0 opacity-100"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="translate-x-0 opacity-100"
    leave-to-class="translate-x-full opacity-0"
    @before-leave="handleBeforeLeave"
  >
    <div
      v-if="visible"
      class="toast"
      :class="toastClasses"
      role="alert"
      aria-live="polite"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <!-- Icon -->
      <div class="toast-icon" :class="iconClasses">
        <svg v-if="toast.type === 'success'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        <svg v-else-if="toast.type === 'error'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
        <svg v-else-if="toast.type === 'warning'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        <svg v-else class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
        </svg>
      </div>

      <!-- Content -->
      <div class="toast-content">
        <h3 class="toast-title">{{ toast.title }}</h3>
        <p v-if="toast.message" class="toast-message">{{ toast.message }}</p>

        <!-- Action Button -->
        <button
          type="button"
          v-if="toast.action"
          @click="handleAction"
          class="toast-action"
        >
          {{ toast.action.label }}
        </button>
      </div>

      <!-- Dismiss Button -->
      <button
        type="button"
        v-if="toast.dismissible"
        @click="handleDismiss"
        class="toast-dismiss"
        aria-label="Cerrar"
      >
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>

      <!-- Progress Bar -->
      <div v-if="showProgress" class="toast-progress">
        <div
          class="toast-progress-bar"
          :class="progressBarClasses"
          :style="{ width: `${progress}%` }"
        />
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import type { Toast } from '@/stores/toast'

interface Props {
  toast: Toast
}

const props = defineProps<Props>()

const emit = defineEmits<{
  dismiss: [id: string]
}>()

// State
const visible = ref(false)
const progress = ref(100)
const isPaused = ref(false)
let progressInterval: ReturnType<typeof setTimeout> | null = null
let startTime = 0
let remainingTime = 0

// Computed
const toastClasses = computed(() => {
  const typeClasses = {
    success: 'toast-success',
    error: 'toast-error',
    warning: 'toast-warning',
    info: 'toast-info'
  }

  return [typeClasses[props.toast.type]]
})

const iconClasses = computed(() => {
  const typeClasses = {
    success: 'text-brand-400',
    error: 'text-red-400',
    warning: 'text-yellow-400',
    info: 'text-blue-400'
  }

  return [typeClasses[props.toast.type]]
})

const progressBarClasses = computed(() => {
  const typeClasses = {
    success: 'bg-brand-400',
    error: 'bg-red-400',
    warning: 'bg-yellow-400',
    info: 'bg-blue-400'
  }

  return [typeClasses[props.toast.type]]
})

const showProgress = computed(() => {
  return props.toast.duration && props.toast.duration > 0
})

// Methods
const startProgress = () => {
  if (!showProgress.value || isPaused.value) return

  const duration = remainingTime || props.toast.duration!
  startTime = Date.now()

  progressInterval = setInterval(() => {
    const elapsed = Date.now() - startTime
    const percentage = Math.max(0, 100 - (elapsed / duration) * 100)
    progress.value = percentage

    if (percentage <= 0) {
      stopProgress()
      handleDismiss()
    }
  }, 10)
}

const stopProgress = () => {
  if (progressInterval) {
    clearInterval(progressInterval)
    progressInterval = null
  }
}

const pauseProgress = () => {
  if (!showProgress.value || isPaused.value) return

  isPaused.value = true
  const elapsed = Date.now() - startTime
  remainingTime = Math.max(0, (remainingTime || props.toast.duration!) - elapsed)
  stopProgress()
}

const resumeProgress = () => {
  if (!showProgress.value || !isPaused.value) return

  isPaused.value = false
  startProgress()
}

const handleDismiss = () => {
  visible.value = false
}

const handleAction = () => {
  props.toast.action?.handler()
  handleDismiss()
}

const handleMouseEnter = () => {
  pauseProgress()
}

const handleMouseLeave = () => {
  resumeProgress()
}

const handleBeforeLeave = () => {
  stopProgress()
  emit('dismiss', props.toast.id)
}

// Lifecycle
onMounted(() => {
  visible.value = true
  startProgress()
})

onUnmounted(() => {
  stopProgress()
})

// Watch for external dismissal
watch(() => props.toast, (newToast, oldToast) => {
  if (newToast.id !== oldToast?.id) {
    visible.value = false
  }
})
</script>

<style scoped>
.toast {
  @apply relative flex items-start gap-3 w-full max-w-sm p-4
         bg-white dark:bg-neutral-800
         rounded-lg shadow-lg
         border border-neutral-200 dark:border-neutral-700
         pointer-events-auto
         overflow-hidden;
}

.toast-success {
  @apply border-brand-200 dark:border-brand-800;
}

.toast-error {
  @apply border-red-200 dark:border-red-800;
}

.toast-warning {
  @apply border-yellow-200 dark:border-yellow-800;
}

.toast-info {
  @apply border-blue-200 dark:border-blue-800;
}

.toast-icon {
  @apply flex-shrink-0;
}

.toast-content {
  @apply flex-1 pt-0.5;
}

.toast-title {
  @apply text-sm font-medium text-neutral-900 dark:text-white;
}

.toast-message {
  @apply mt-1 text-sm text-neutral-500 dark:text-neutral-400;
}

.toast-action {
  @apply mt-3 text-sm font-medium text-brand-600 hover:text-brand-500
         dark:text-brand-400 dark:hover:text-brand-300
         focus:outline-none focus:underline;
}

.toast-dismiss {
  @apply flex-shrink-0 ml-4 inline-flex h-11 w-11 items-center justify-center text-neutral-400 hover:text-neutral-500
         dark:text-neutral-500 dark:hover:text-neutral-400
         focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
         rounded-md;
}

.toast-progress {
  @apply absolute bottom-0 left-0 right-0 h-1 bg-neutral-200 dark:bg-neutral-700 overflow-hidden;
}

.toast-progress-bar {
  @apply h-full transition-all duration-100 ease-linear;
}
</style>
